import { SEGMENTATION } from "constants/segmentation";
import { ROLES } from "constants/authorization";

/**
 * Converts sponsor's segment attributes into expected format for Dropdown component
 * @param {string} segmentFeatureFlag Value returned from feature flag (ex: "officeLocation")
 * @param {array} segmentAttributes The sponsor's segment attributes, returned from the Segmentation resource
 * @returns {Object[]} List of dropdown options [{ label: "officeLocation", value: "San Francisco" }, {...}]
 */
export function formatSegmentValuesForDropdown(segmentFeatureFlag = "", segmentAttributes = []) {
  if (segmentAttributes && segmentAttributes.length) {
    const segmentAttribute = segmentAttributes.filter(segment => {
      return (
        segment.attributeName &&
        segment.attributeName.toLowerCase() === segmentFeatureFlag.toLowerCase() &&
        segment.values &&
        segment.values.length
      );
    })[0];
    const mappedValues =
      segmentAttribute &&
      segmentAttribute.values.map(attribute => {
        return {
          label: attribute.description || "",
          value: attribute.value || ""
        };
      });
    if (mappedValues && mappedValues.length > 1) {
      mappedValues.unshift({
        label: SEGMENTATION.ALL,
        value: SEGMENTATION.ALL
      });
    }
    return mappedValues;
  }
  return [];
}

/**
 * Gets the sponsorAdmin's segment attribute value (if it matches their sponsor's segmentation configuration)
 * @param {string} segmentFeatureFlag Value returned from feature flag (ex: "officeLocation")
 * @param {object} sponsorAdmin The compass user currently logged in
 * @returns {string} (ex: "San Francisco")
 */
export function getAdminSegmentAttribute(segmentFeatureFlag, sponsorAdmin) {
  let value = "";
  if (
    segmentFeatureFlag &&
    sponsorAdmin.segmentGroups &&
    sponsorAdmin.segmentGroups.length &&
    sponsorAdmin.segmentGroups[0].attributes &&
    sponsorAdmin.segmentGroups[0].attributes.length &&
    sponsorAdmin.segmentGroups[0].attributes[0].value &&
    sponsorAdmin.segmentGroups[0].attributes[0].name &&
    sponsorAdmin.segmentGroups[0].attributes[0].name.toLowerCase() ===
      segmentFeatureFlag.toLowerCase()
  ) {
    value = sponsorAdmin.segmentGroups[0].attributes[0].value;
  }
  return value;
}

/**
 * Determines whether the sponsor has segmented access configured via feature flag
 * and whether sponsorAdmin has a segmentGroup matching their sponsor's segment configuration
 * Note: an empty segmentGroup indicates full access
 * @param {string} segmentFeatureFlag Value returned from feature flag (ex: "officeLocation")
 * @param {object} sponsorAdmin The compass user currently logged in
 * @returns {boolean}
 */
export function adminHasRestrictedSegmentedAccess(segmentFeatureFlag = "", sponsorAdmin = {}) {
  return Boolean(
    segmentFeatureFlag &&
      sponsorAdmin.segmentGroups &&
      sponsorAdmin.segmentGroups.length &&
      sponsorAdmin.segmentGroups.some(segment => {
        return (
          segment.attributes &&
          segment.attributes.length &&
          segment.attributes[0].name &&
          segment.attributes[0].name.toLowerCase() === segmentFeatureFlag.toLowerCase()
        );
      })
  );
}

/** Constructs the segmentGroups array to be included in the request payload for adding a new team member
 * @param {string} segmentFeatureFlag Value returned from feature flag (ex: "officeLocation")
 * @param {string} selectedSegment Value selected from the SelectSegment dropdown menu
 * @returns {array|undefined}
 */
export function constructSegmentGroupsForNewAdmin(segmentFeatureFlag = "", selectedSegment = "") {
  if (segmentFeatureFlag && selectedSegment) {
    return selectedSegment === SEGMENTATION.ALL
      ? [] // Empty array indicates full access
      : [
          {
            attributes: [
              {
                name: segmentFeatureFlag,
                value: selectedSegment
              }
            ]
          }
        ];
  }
  return undefined;
}

/**
 * Handles a selection from the segment dropdown menu in AddTeamMember.js / EditTeamMember.js
 * @param newSegmentValue New value selected from segment dropdown menu (to be set in state)
 * @param currentSegmentValue Previous value selected from segment dropdown menu (currently set in state)
 * @param setSegment Callback to set component's selectedSegment state
 * @param setRole Callback to set component's accessLevel / sponsorGroupType state (AKA "role")
 */
export function handleSegmentSelect(newSegmentValue, currentSegmentValue, setSegment, setRole) {
  switch (newSegmentValue) {
    // If the same segment value is re-selected, clear segment and role
    case currentSegmentValue:
      setSegment("");
      setRole();
      break;
    // If the "All" segment is selected, set segment to "All" and clear role
    // If the "All" segment is re-selected, clear segment and role
    case SEGMENTATION.ALL:
      setSegment(newSegmentValue === currentSegmentValue ? "" : SEGMENTATION.ALL);
      setRole();
      break;
    // If a non-"All" segment is selected, set segment to newSegmentValue and role to "People"
    default:
      setSegment(newSegmentValue);
      setRole(ROLES.PEOPLE);
      break;
  }
}
